import type { AxiosInstance } from 'axios';

import type { AcceptQuoteInput } from './quotes';

/** Supplier payout onboarding state, mirrored from the API's ConnectStatusDto. */
export interface ConnectStatus {
  /** Whether a Stripe connected account exists for this supplier. */
  hasAccount: boolean;
  /** Whether payouts can be received (stripe_transfers capability is active). */
  payoutsEnabled: boolean;
  /** Whether the supplier finished the hosted onboarding form. */
  detailsSubmitted: boolean;
  /** Raw transfers capability status: inactive | pending | active | restricted | unsupported. */
  transfersStatus: string;
}

interface ConnectUrl {
  url: string;
}

/** A Stripe-hosted Checkout Session, ready to redirect the buyer to. */
export interface CheckoutSession {
  url: string;
  sessionId: string;
}

/**
 * Where a checkout attempt stands. `orderId` is null until the
 * `checkout.session.completed` webhook has created the order — the browser's
 * return from Stripe can beat it there.
 */
export interface CheckoutSessionStatus {
  paymentStatus: string;
  orderId: string | null;
}

/**
 * Stripe Connect (supplier payouts) and buyer Checkout endpoints. Onboarding,
 * the Express Dashboard, and Checkout itself are all Stripe-hosted: these
 * return single-use URLs the app redirects to, or reads back Stripe's own
 * state for.
 */
export function createPaymentsService(client: AxiosInstance) {
  return {
    async getConnectStatus(): Promise<ConnectStatus> {
      const { data } = await client.get<ConnectStatus>('/payments/connect/status');
      return data;
    },

    /** Create/continue onboarding; returns the Stripe-hosted onboarding URL. */
    async startConnectOnboarding(): Promise<string> {
      const { data } = await client.post<ConnectUrl>('/payments/connect/onboarding');
      return data.url;
    },

    /** Returns a single-use login link to the supplier's Express Dashboard. */
    async getConnectDashboardLink(): Promise<string> {
      const { data } = await client.post<ConnectUrl>('/payments/connect/dashboard-link');
      return data.url;
    },

    /**
     * Starts a Stripe Checkout Session for a quote. The ship-to address and
     * chosen shipping rate ride along in `body` — the order is created only
     * once the webhook confirms payment, so nothing here is persisted yet.
     */
    async createCheckoutSession(
      body: { quoteId: string } & AcceptQuoteInput,
    ): Promise<CheckoutSession> {
      const { data } = await client.post<CheckoutSession>('/payments/checkout/session', body);
      return data;
    },

    /** Resolves a Checkout Session id to the order created from it, once it exists. */
    async getCheckoutSessionStatus(sessionId: string): Promise<CheckoutSessionStatus> {
      const { data } = await client.get<CheckoutSessionStatus>(
        `/payments/checkout/session/${sessionId}`,
      );
      return data;
    },
  };
}

export type PaymentsService = ReturnType<typeof createPaymentsService>;
